import { Button, CircularProgress } from '@civilgrid/ui/components';
import { useEffect, useRef, useState } from 'react';
import { getOrganization, getSpecialDatasetSlugs, updateOrgSpecialDatasets } from '~/api/internal';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { OrganizationDetail } from '~/common/types';
import { Notify } from '~/components2/Notify';
import OrganizationPicker from '~/components2/OrganizationPicker';
import { MajorHeading } from '../common/Heading';
import { addSpecialDatasetSlug, removeSpecialDatasetSlug } from './specialDatasets';

export default function OrgSpecialDatasets() {
  const orgRequestSeq = useRef(0);
  const selectedOrgIdRef = useRef<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDetail | null>(null);
  const [slugs, setSlugs] = useState<string[]>([]);
  // Snapshot of the slugs as last loaded/saved from the server, to detect unsaved edits.
  const [initialSlugs, setInitialSlugs] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [newSlug, setNewSlug] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(false);

  useEffect(() => {
    getSpecialDatasetSlugs()
      .then((s) => {
        setSuggestions(s);
        setSuggestionsError(false);
      })
      .catch((e) => {
        console.error('Error loading special dataset suggestions:', e);
        setSuggestionsError(true);
      });
  }, []);

  const handleOrganizationSelect = async (organizationId: string) => {
    const seq = ++orgRequestSeq.current;
    try {
      setIsLoading(true);
      const org = await getOrganization(organizationId);
      if (seq !== orgRequestSeq.current || org.id !== organizationId) return;
      const loaded = Array.isArray(org.specialDatasets) ? org.specialDatasets.map(String) : [];
      selectedOrgIdRef.current = org.id;
      setSelectedOrg(org);
      setSlugs(loaded);
      setInitialSlugs(loaded);
    } catch (e) {
      if (seq !== orgRequestSeq.current) return;
      console.error('Error loading organization:', e);
      Notify.error({ message: 'Error loading organization details' });
    } finally {
      if (seq === orgRequestSeq.current) setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedOrg) return;
    const orgIdAtSave = selectedOrg.id;
    try {
      setIsSaving(true);
      await updateOrgSpecialDatasets(orgIdAtSave, slugs);
      capturePosthogEvent(POSTHOG_EVENTS.orgSpecialDatasetsUpdated, {
        organization_id: orgIdAtSave,
        special_datasets: slugs,
        special_datasets_count: slugs.length,
      });
      // Adding the saved slugs to the global suggestion list is org-independent, so it's safe
      // even if the user switched orgs mid-save.
      setSuggestions((prev) => Array.from(new Set([...prev, ...slugs])).sort());
      Notify.info({ message: `Special datasets updated for ${selectedOrg.name}` });
      // Only commit the saved baseline if this is still the active org; otherwise a stale save
      // completing after an org switch would overwrite the new org's dirty-state baseline.
      if (selectedOrgIdRef.current === orgIdAtSave) {
        setInitialSlugs(slugs);
      }

      // Best-effort sync with persisted state. Save already succeeded, so a refresh failure must
      // not surface as a save error — isolate it.
      try {
        const refreshed = await getOrganization(orgIdAtSave);
        if (selectedOrgIdRef.current !== orgIdAtSave || refreshed.id !== orgIdAtSave) return;
        const persisted = Array.isArray(refreshed.specialDatasets) ? refreshed.specialDatasets.map(String) : [];
        setSelectedOrg(refreshed);
        setSlugs(persisted);
        setInitialSlugs(persisted);
      } catch (refreshError) {
        console.error('Error refreshing organization after save:', refreshError);
      }
    } catch (e) {
      console.error('Error updating special datasets:', e);
      Notify.error({ message: 'Error updating special datasets. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const available = suggestions.filter((s) => !slugs.includes(s));
  // Compare as sets — order doesn't matter, both lists are deduped.
  const isDirty =
    slugs.length !== initialSlugs.length ||
    slugs.some((s) => !initialSlugs.includes(s)) ||
    initialSlugs.some((s) => !slugs.includes(s));

  return (
    <div className="space-y-4 mb-10">
      <div className="space-y-2">
        <MajorHeading>Select Organization</MajorHeading>
        <div className="flex items-center space-x-2">
          <span className="font-semibold">Organization:</span>
          <OrganizationPicker
            selectedOrganization={selectedOrg}
            onOrganizationSelect={(organizationId) => handleOrganizationSelect(organizationId)}
          />
        </div>
      </div>

      {isLoading && <CircularProgress indeterminate />}

      {selectedOrg && !isLoading && (
        <div className="space-y-4">
          <MajorHeading>Special Datasets</MajorHeading>

          <div className="flex flex-wrap gap-2" data-testid="org-special-datasets-chips">
            {slugs.length === 0 && <span className="text-sm text-grey-60">No special datasets assigned.</span>}
            {slugs.map((slug) => (
              <span key={slug} className="inline-flex items-center gap-1 rounded bg-grey-30 px-2 py-0.5 text-sm">
                {slug}
                <button
                  type="button"
                  aria-label={`Remove ${slug}`}
                  data-testid={`org-special-datasets-remove-${slug}`}
                  onClick={() => setSlugs((prev) => removeSpecialDatasetSlug(prev, slug))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="flex items-center space-x-2">
            <span className="font-semibold">Add existing:</span>
            <select
              data-testid="org-special-datasets-suggestions"
              value=""
              disabled={available.length === 0}
              onChange={(e) => {
                if (e.target.value) setSlugs((prev) => addSpecialDatasetSlug(prev, e.target.value));
              }}
            >
              <option value="">Select a dataset…</option>
              {available.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {suggestionsError && (
              <span className="text-sm text-red-60" data-testid="org-special-datasets-suggestions-error">
                Couldn't load existing datasets — you can still add new ones below.
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <span className="font-semibold">Add new:</span>
            <input
              data-testid="org-special-datasets-new-input"
              className="border rounded px-2 py-1 text-sm"
              value={newSlug}
              placeholder="e.g. westModesto"
              onChange={(e) => setNewSlug(e.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              data-testid="org-special-datasets-add"
              onClick={() => {
                setSlugs((prev) => addSpecialDatasetSlug(prev, newSlug));
                setNewSlug('');
              }}
            >
              Add
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              type="button"
              data-testid="org-special-datasets-save"
              loading={isSaving}
              disabled={isSaving || !isDirty}
              onClick={handleSave}
            >
              Save Special Datasets
            </Button>
            {isDirty && (
              <span className="text-sm text-grey-60" data-testid="org-special-datasets-unsaved">
                Unsaved changes
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
